import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { IndianRupee } from "lucide-react";

/** Razorpay button. Loads checkout.js dynamically. If configured=false, runs in mock mode. */
export default function RazorpayButton({ lookupKey, title, className = "", onSuccess }) {
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("mock");

  useEffect(() => {
    api.get("/payments/razorpay/config").then(r => setMode(r.data.mode)).catch(() => {});
    const existing = document.getElementById("razorpay-checkout-js");
    if (existing) { setReady(true); return; }
    const s = document.createElement("script");
    s.id = "razorpay-checkout-js";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => setReady(true);
    s.onerror = () => setReady(false);
    document.body.appendChild(s);
  }, []);

  const pay = async () => {
    setBusy(true);
    try {
      const { data: order } = await api.post("/payments/razorpay/order", { lookup_key: lookupKey, quantity: 1 });
      if (order.mode === "mock" || !window.Razorpay) {
        // simulate success
        await api.post("/payments/razorpay/verify", {
          razorpay_order_id: order.order_id,
          razorpay_payment_id: `mock_pay_${Date.now()}`,
          razorpay_signature: "mock_sig",
        });
        toast.success(`Mock payment complete for ${title}. Wire your Razorpay keys to accept real payments.`);
        onSuccess && onSuccess();
        return;
      }
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "TBVH",
        description: title,
        order_id: order.order_id,
        handler: async (resp) => {
          try {
            const { data: verify } = await api.post("/payments/razorpay/verify", resp);
            if (verify.verified) { toast.success("Payment verified."); onSuccess && onSuccess(); }
            else toast.error("Signature verification failed.");
          } catch { toast.error("Verify failed."); }
        },
        theme: { color: "#FF3333" },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch {
      toast.error("Could not start Razorpay order.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={pay}
      disabled={busy}
      className={`btn-outline text-xs inline-flex items-center gap-2 ${className}`}
      data-testid={`razorpay-btn-${lookupKey}`}
      title={mode === "mock" ? "Razorpay in mock mode — add keys for live payments" : "Pay with Razorpay"}
    >
      <IndianRupee size={12} /> {busy ? "…" : mode === "mock" ? "Pay via Razorpay (mock)" : "Pay via Razorpay"}
    </button>
  );
}
